<template>
    <div class="mark-info m-i-model" v-show="visible">
        <!-- 标题栏 -->
        <div class="m-i-title">
            <div class="txt">标注信息</div>
            <div class="close-icon" @click="handleClose">
                <CloseOutlined />
            </div>
        </div>

        <!-- 名称和操作 -->
        <div class="m-i-name">
            <div class="icon">
                <MarkIcons type="point" color="#2d8cf0" />
            </div>
            <div class="name">
                <a-input v-model:value="current.localName"  size="small" :maxlength="28"
                    placeholder="请输入标注名称" @change="handleNameChange" />
            </div>
            <div class="view" @click="handleView">
                <AimOutlined />
            </div>
            <a-popconfirm title="是否确定删除当前图形?" okText="确定" okType="danger" @confirm="handleDelete">
                <div class="del">
                    <DeleteOutlined />
                </div>
            </a-popconfirm>
        </div>

        <a-divider />

        <!-- 测量数据 -->
        <a-divider></a-divider>

        <div class="m-i-measure-info">

            <!-- 经度 -->
            <div class="m-i-li">
                <div class="key">经度</div>
                <div class="val">
                    {{ current.longitude }}°
                </div>

            </div>

            <!-- 纬度 -->
            <div class="m-i-li">
                <div class="key">纬度</div>
                <div class="val">
                    {{ current.latitude }}°
                </div>
            </div>

            <div class="m-row-li">
                <div class="key">角度</div>
                <div class="val">
                    <div class="ant-input-number-group">
                        <div class="ant-input-number-group-addon" @click="handleChangeVal('mini', 'rotate')">
                            <left-outlined />
                        </div>
                        <a-input-number v-model:value="current.rotate" size="small"  :controls="false"
                            @change="handleRotateChange" />
                        <div class="ant-input-number-group-addon" @click="handleChangeVal('plus', 'rotate')">
                            <right-outlined />
                        </div>
                    </div>
                </div>
            </div>

            <div class="m-row-li">
                <div class="key">缩放比例</div>
                <div class="val">
                    <div class="ant-input-number-group">
                        <div class="ant-input-number-group-addon" @click="handleChangeVal('mini', 'scale')">
                            <left-outlined />
                        </div>
                        <a-input-number v-model:value="current.scale" size="small" :min="0.1" :max="10"
                            :controls="false"  @change="handleScaleChange" />
                        <div class="ant-input-number-group-addon" @click="handleChangeVal('plus', 'scale')">
                            <right-outlined />
                        </div>
                    </div>
                </div>
            </div>

            <div class="m-i-li">

                <div class="val">
                    <a-checkbox  v-model:checked="current.showNameOnMap"
                        @change="handleChangeCheckbox('showNameOnMap')">在地图中显示名称</a-checkbox>
                </div>
            </div>
            <div class="m-i-li">

                <div class="val">
                    <a-checkbox  v-model:checked="current.ignoreOcclusion"
                        @change="handleChangeCheckbox('ignoreOcclusion')">标签无视物体遮挡进行展示</a-checkbox>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, toRefs } from 'vue'
import MarkIcons from '/@/components/mark-info/icons.vue'
import { useMyStore } from '/@/store'
import * as Cesium from 'cesium'

// eslint-disable-next-line no-undef
const props = defineProps<{
    visible: boolean;
    markList: any[];
    row: any;
    drawTool: any;
    viewer: any

}>()

// eslint-disable-next-line no-undef
const emits = defineEmits(['update:visible', 'delete-mark', 'updateMarkList', 'updateCurrent'])
const store = useMyStore()
const { viewer, visible, row: current, markList, drawTool } = toRefs(props)

const handleView = () => {
  viewer.value?.camera.flyTo({
    destination: Cesium.Cartesian3.fromDegrees(
      parseFloat(current.value.longitude),
      parseFloat(current.value.latitude),
      parseFloat(current.value.altitude) + 100
    ),
    duration: 1.5
  })
}
const handleClose = () => {
  emits('update:visible', false)
  drawTool.value.restoreCameraSettings()
}
const handleDelete = () => {
  emits('delete-mark', current.value)
}
// 辅助函数：查找当前项（支持父子层级）
const findCurrentItem = (list, id) => {
  const item = list.find(el => el.id === id)
  if (item) return { item, isChild: false }
  for (let i = 0; i < list.length; i++) {
    const child = list[i].childItems?.find(e => e.id === id)
    if (child) return { item: child, isChild: true, parentIndex: i, childIndex: list[i].childItems.findIndex(e => e.id === id) }
  }
  return null
}

const handleNameChange = () => {
  const result = findCurrentItem(markList.value, current.value.id)
  if (!result) return
  const { item: row, isChild } = result
  row.iconName = current.value.localName
  row.localName = current.value.localName
  row.rotate = current.value.rotate
  row.scale = current.value.scale
  if (isChild) {
    markList.value[result.parentIndex].childItems[result.childIndex] = row
  } else {
    const index = markList.value.findIndex(el => el.id === current.value.id)
    markList.value.splice(index, 1, row)
  }
  emits('updateCurrent', row)
  emits('updateMarkList', markList.value)
}

const handleChangeCheckbox = (key) => {
  nextTick(() => {
    const result = findCurrentItem(markList.value, current.value.id)
    if (!result) return
    const { item: row, isChild } = result
    row[key] = current.value[key]
    row.rotate = current.value.rotate
    row.scale = current.value.scale
    row.iconName = current.value.localName
    row.localName = current.value.localName
    if (isChild) {
      markList.value[result.parentIndex].childItems[result.childIndex] = row
    } else {
      const index = markList.value.findIndex(el => el.id === current.value.id)
      markList.value.splice(index, 1, row)
    }
    emits('updateCurrent', row)
    emits('updateMarkList', markList.value)
  })
}

const handleChangeVal = (type, key) => {
  const result = findCurrentItem(markList.value, current.value.id)
  if (!result) return
  const { item: row, isChild } = result
  if (type === 'mini') {
    if (key === 'rotate') {
      row[key] -= 1
    } else {
      row[key] -= 1
      if (row[key] < 0.1) {
        row[key] = 0.1
      }
    }
  } else {
    if (key === 'rotate') {
      row[key] += 1
    } else {
      row[key] += 1
      if (row[key] > 10) {
        row[key] = 10
      }
    }
  }
  row.iconName = current.value.localName
  row.localName = current.value.localName
  if (key === 'rotate') {
    row.scale = current.value.scale
  } else {
    row.rotate = current.value.rotate
  }
  if (isChild) {
    markList.value[result.parentIndex].childItems[result.childIndex] = row
  } else {
    const index = markList.value.findIndex(el => el.id === current.value.id)
    markList.value.splice(index, 1, row)
  }
  emits('updateCurrent', row)
  emits('updateMarkList', markList.value)
}
const handleRotateChange = () => {
  nextTick(() => {
    const result = findCurrentItem(markList.value, current.value.id)
    if (!result) return
    const { item: row, isChild } = result
    row.rotate = current.value.rotate
    row.scale = current.value.scale
    row.iconName = current.value.localName
    row.localName = current.value.localName
    if (isChild) {
      markList.value[result.parentIndex].childItems[result.childIndex] = row
    } else {
      const index = markList.value.findIndex(el => el.id === current.value.id)
      markList.value.splice(index, 1, row)
    }
    emits('updateCurrent', row)
    emits('updateMarkList', markList.value)
  })
}
const handleScaleChange = () => {
  nextTick(() => {
    const result = findCurrentItem(markList.value, current.value.id)
    if (!result) return
    const { item: row, isChild } = result
    row.scale = current.value.scale
    row.rotate = current.value.rotate
    row.iconName = current.value.localName
    row.localName = current.value.localName
    if (isChild) {
      markList.value[result.parentIndex].childItems[result.childIndex] = row
    } else {
      const index = markList.value.findIndex(el => el.id === current.value.id)
      markList.value.splice(index, 1, row)
    }
    emits('updateCurrent', row)
    emits('updateMarkList', markList.value)
  })
}
</script>

<style scoped lang="scss">
.ant-input-number-group {
    display: inline-block;
    text-align: start;
    vertical-align: top;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    color: rgba(0,0,0, 0.85);
    font-size: 14px;
    line-height: 1.5714285714285714;
    list-style: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
    position: relative;
    display: table;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    .ant-input-number-group-addon {
        display: table-cell;
        width: 1px;
        white-space: nowrap;
        vertical-align: middle;
        position: relative;
        padding: 0 11px;
        color: rgba(0,0,0, 0.85);
        font-weight: normal;
        font-size: 14px;
        text-align: center;
        background-color: rgba(255,255,255, 0.04);
        border: 1px solid #424242;
        border-radius: 2px;
        transition: all 0.3s;
        line-height: 1;
        cursor: pointer;

        &:first-child {
            border-start-end-radius: 0;
            border-end-end-radius: 0;
            border-inline-end: 0;
        }
    }
}

.mark-info {
    position: absolute;
    left: 330px;
    top: 10%;
    z-index: 301;
    background: #ffffff;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(255, 255, 255, 0.65);
    padding: 16px;
    width: 260px;
}

.m-i-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .txt {
        font-size: 14px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.85);
        pointer-events: none;
    }

    .close-icon {
        cursor: pointer;
        color: rgba(0, 0, 0, 0.45);
        transition: color 0.3s;

        &:hover {
            color: rgba(0, 0, 0, 0.85);
        }
    }
}

.m-i-name {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    .icon {
        flex-shrink: 0;
    }

    .name {
        flex: 1;

        :deep(.ant-input) {
            font-size: 12px;
        }
    }

    .view,
    .del {
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.3s;

        &:hover {
            background-color: rgba(255,255,255, 0.06);
        }
    }

    .view {
        color: #2d8cf0;
    }

    .del {
        color: #ff4d4f;
    }
}

.m-i-color {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .txt {
        font-size: 14px;
        color: rgba(0, 0, 0, 0.85);
    }

    .color {
        display: flex;
        gap: 8px;

        .c-li {
            width: 20px;
            height: 20px;
            border-radius: 4px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s;

            &:hover {
                transform: scale(1.1);
            }
        }
    }
}

.m-i-measure-txt {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 1);
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 4px;

    .anticon-exclamation-circle {
        color: #faad14;
    }
}

.m-i-measure-info {
    .m-row-li {
        display: flex;
        flex-direction: column;
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }

        .key {
            font-size: 12px;
            color: rgba(0, 0, 0, 0.65);
            flex-shrink: 0;
            margin-bottom: 5px;
            pointer-events: none;
        }

        .val {
            font-size: 12px;
            color: rgba(0, 0, 0, 0.85);
            display: flex;
            align-items: center;
            gap: 4px;
        }
    }

    .m-i-li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }

        .key {
            font-size: 12px;
            color: rgba(0, 0, 0, 0.65);
            flex-shrink: 0;
            pointer-events: none;
        }

        .val {
            font-size: 12px;
            color: rgba(0, 0, 0, 0.85);
            display: flex;
            align-items: center;
            gap: 4px;

            :deep(.ant-input) {
                font-size: 12px;
            }
        }
    }
}

:deep(.ant-divider) {
    margin: 14px -16px;
    min-width: auto;
}
</style>
